<template>
  <div class="h-100 w-full">
    <div ref="map" class="size-full" />
  </div>
</template>

<script>
import { useRuntimeConfig } from 'nuxt/app'
import { removeClass, toggleClass } from '~/utils/class'

const markerActive = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="250 250 360 360"><defs><clipPath id="clip-path"><rect class="cls-1" x="300.02" y="255.11" width="267.27" height="354.88"/></clipPath></defs><g id="Layer_3" data-name="Layer 3"><g class="cls-2"><path class="cls-3" d="M433.66,269.56c-65.7,0-119.19,53.65-119.19,119.56,0,83.45,74.67,166.26,111.51,203.22a10.82,10.82,0,0,0,15.33,0l0,0c36.85-37,111.52-119.77,111.52-203.22,0-65.91-53.49-119.56-119.19-119.56"/><path class="cls-4" d="M433.66,610h0a25.12,25.12,0,0,1-17.89-7.46C363,549.58,300,469.57,300,389.12c0-73.9,60-134,133.64-134s133.64,60.11,133.64,134c0,80.31-62.72,160.19-115.46,213.15l-.31.32A25.13,25.13,0,0,1,433.66,610Zm0-326c-57.76,0-104.74,47.15-104.74,105.11,0,38.57,18.26,102.81,104.74,190.44C520.14,491.93,538.4,427.7,538.4,389.12,538.4,331.16,491.41,284,433.66,284Z"/></g></g></svg>'
let mapboxgl

export default {
  props: {
    destinations: { type: Array, required: true },
    fitBoundsInitial: { type: Boolean, default: false },
  },

  emits: ['markerClicked'],

  data: () => {
    return {
      mapStyle: 'mapbox://styles/tripexpertweb/ckebanle30s1d1aqt71v6sp4m',
      map: null,
      mapLoaded: false,
      markers: {},
      markersOnScreen: [],
    }
  },

  computed: {
    geoData () {
      return {
        type: 'FeatureCollection',
        features: this.destinations.map(d => ({
          type: 'Feature',
          properties: { id: d.id, title: d.name, path: d.path },
          geometry: {
            type: 'Point',
            coordinates: [d.longitude, d.latitude],
          },
        })),
      }
    },

    bounds () {
      return [
        [
          Math.min(...this.destinations.map(v => v.longitude)),
          Math.min(...this.destinations.map(v => v.latitude)),
        ],
        [
          Math.max(...this.destinations.map(v => v.longitude)),
          Math.max(...this.destinations.map(v => v.latitude)),
        ],
      ]
    },
  },

  watch: {
    destinations () {
      if (this.mapLoaded) {
        this.addMarkers()
        this.updateMarkers()
        this.fitBounds()
      }
    },
  },

  mounted () {
    this.initMap()
  },

  methods: {
    async initMap () {
      mapboxgl = (await import('mapbox-gl')).default
      mapboxgl.accessToken = useRuntimeConfig().public.mapboxApiToken
      this.map = new mapboxgl.Map({
        container: this.$refs.map,
        style: 'mapbox://styles/tripexpertweb/ckcdpjifn06ru1impw321xsqi',
        center: [0, 48],
        zoom: 2,
      })
      this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left')
      this.map.scrollZoom.disable()
      this.map.on('load', () => {
        this.mapLoaded = true
        this.addMarkers()
        this.updateMarkers()
      })
      this.fitBoundsInitial && setTimeout(() => this.fitBounds(), 300)
    },

    addMarkers () {
      if (! process.client) { return }
      if (this.markers.length) {
        Object.keys(this.markers).forEach(id => this.markers[id].remove())
        this.markers = {}
      }
      this.markersOnScreen = []

      if (this.map.getLayer('clusters')) { this.map.removeLayer('clusters') }
      if (this.map.getLayer('cluster-count')) { this.map.removeLayer('cluster-count') }

      const source = this.map.getSource('destinations')
      if (source) {
        source.setData(this.geoData)
      } else {
        this.map.addSource('destinations', {
          type: 'geojson',
          data: this.geoData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        })
      }

      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'destinations',
        filter: ['has', 'point_count'],
        paint: {
          // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            100,
            '#f1f075',
            750,
            '#f28cb1',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40,
          ],
        },
      })

      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'destinations',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      })

      // after the GeoJSON data is loaded, update markers on the screen and do so on every map move/moveend
      this.map.on('data', (e) => {
        if (e.sourceId !== 'destinations' || ! e.isSourceLoaded) { return }

        this.map.on('move', this.updateMarkers)
        this.map.on('moveend', this.updateMarkers)
        this.updateMarkers()
      })

      this.map.on('click', 'clusters', (e) => {
        const cluster = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
        this.map.flyTo({
          center: cluster[0].geometry.coordinates,
          zoom: this.map.getZoom() + 1,
        })
      })
    },

    updateMarkers () {
      const newMarkers = {}
      const destinations = this.map.querySourceFeatures('destinations')

      // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
      // and add it to the map if it's not there already
      for (let i = 0; i < destinations.length; i++) {
        const coords = destinations[i].geometry.coordinates
        const props = destinations[i].properties
        if (props.cluster) { continue }
        const id = props.id

        let marker = this.markers[id]
        if (! marker) {
          const el = document.createElement('div')
          el.innerHTML = `<div class="marker-inner">${markerActive}</div>`
          el.id = `marker-${props.id}`
          el.className = 'marker acttive'
          // el.addEventListener('click', () => this.$router.push(props.path))

          marker = this.markers[id] = new mapboxgl.Marker({
            element: el,
          }).setLngLat(coords)

          marker.setPopup(new mapboxgl.Popup({
            offset: {
              'top': [0, 10],
              'bottom': [0, -60],
              'left': [30, -23],
              'right': [-30, -23],
              'top-left': [25, 0],
              'top-right': [-25, 0],
              'bottom-left': [25, -50],
              'bottom-right': [-25, -50],
            },
            closeButton: false,
            closeOnMove: false,
            maxWidth: '300px',
          })
            .setHTML(`
              <a href="/${props.path}" class="flex items-center">
                <div>
                  <h3 class="font-whitney-condensed break-words">${props.title}</h3>
                </div>
                <div class="chevron-right">
                  <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.29936 16L-5.6797e-08 14.7006L6.70063 8L-6.42585e-07 1.29937L1.29936 -5.6797e-08L9.29936 8L1.29936 16Z" fill="white"/>
                  </svg>
                </div>
              </a>
           `))
        }
        newMarkers[id] = marker

        if (! this.markersOnScreen[id]) { marker.addTo(this.map) }
      }
      // for every marker we've added previously, remove those that are no longer visible
      for (const id in this.markersOnScreen) {
        if (! newMarkers[id]) { this.markersOnScreen[id].remove() }
      }
      this.markersOnScreen = newMarkers
    },

    fitBounds (duration = 1000) {
      if (! this.destinations.length) { return }
      this.map.fitBounds(this.bounds, { padding: 30, duration: this.firstLoad ? 0 : duration })
      this.map.once('moveend', () => (this.preventShowSearchArea = false))
    },

    toggleMarkerActive (el) {
      this.markers.forEach((m) => {
        removeClass(m.getElement(), 'active')
      })
      if (el) { toggleClass(el, 'active') }
    },

    markerClick (index) {
      this.$emit('markerClicked', index)
    },
  },
}
</script>

<style lang="scss" scoped>
  :deep() {
    .mapboxgl-popup-content {
      @apply p-4 bg-slate text-white rounded-lg;
      h3 {
        @apply text-lg uppercase font-medium;
        letter-spacing: 0.1em;

        @media (max-width: 912px) { @apply text-sm; }
      }
      p {
        @apply text-sm;
      }
      .chevron-right {
        @apply block pl-6 pr-1;
      }
    }

    .mapboxgl-popup-tip { display: none; }

    .marker {
      @apply w-12 h-12 font-medium text-body text-center cursor-pointer;
      font-size: 22px; margin-top: -24px; line-height: 30px;

      .cls-1 { fill: none; }
      .cls-2 { clip-path: url(#clip-path); }
      .cls-3 { fill: #daf602; }
      .cls-4 { fill: #84857b; }
    }
    .marker-inner {
      position: relative; width: 100%; height: 100%;
      img { max-height: 100%; margin: auto; }
    }
    .maker-score {
      @apply absolute inset-0 pt-1 leading-normal;
    }

    .mapboxgl-ctrl-top-left .mapboxgl-ctrl {
      @apply mt-3;
    }
  }
</style>
